<template>
  <layout>
    <div
      class="space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.location.spaces") }}
        </h1>
        <primary-button
          v-if="hasPermission(permissions.createSpace)"
          class="hidden sm:flex"
          :label="t('admin.settings.location.add_space')"
          @click="openSpaceSlideOver = true"
        />
        <PlusIcon
          v-if="hasPermission(permissions.createSpace)"
          class="w-10 text-gray-900 sm:hidden -mr-1"
          @click="openSpaceSlideOver = true"
        />
      </div>
    </div>
    <div class="flex flex-col dynamic-height overflow-auto">
      <tree
        :tenant-id="tenant.id"
        :model-value="props.spaces"
        :initial-parent-id="props.space?.parent_id"
        class="bg-white location-tree"
        @clicked="handleSpaceSelected"
      />
      <div v-if="props.spaces?.length === 0" class="flex justify-center mt-24">
        <div class="flex-col space-y-8 text-center px-12 max-w-2xl">
          <div class="flex-col space-y-2">
            <img src="/images/empty/spaces.svg" class="mx-auto w-36" />
            <h1 class="text-2xl font-semibold text-gray-900">
              {{ t("components.empty_states.spaces.title") }}
            </h1>
            <p class="text-gray-600">
              {{ t("components.empty_states.spaces.description") }}
            </p>
          </div>
          <primary-button
            v-if="hasPermission(permissions.createSpace)"
            :label="t('admin.settings.location.add_space')"
            @click="openSpaceSlideOver = true"
          />
        </div>
      </div>
    </div>

    <settings-panel
      :open="openSpaceSlideOver"
      :details="parsedLocationDetails"
      :fields="spaceFields"
      :canCreate="hasPermission(permissions.createSpace)"
      :canUpdate="hasPermission(permissions.updateSpace)"
      :canDelete="hasPermission(permissions.deleteSpace)"
      :createLabel="t('admin.settings.location.add_space')"
      :updateLabel="t('admin.settings.location.update_space')"
      :is-create-loading="isCreateLoading"
      :is-update-loading="isUpdateLoading"
      :is-delete-loading="isDeleteLoading"
      @close="handleCloseSpaceSlideOver"
      @create="handleCreateSpace"
      @update="handleUpdateSpace"
      @delete="handleDeleteSpace"
      :deleteConfirmation="{
        title: t('admin.settings.edit_space.delete.confirmation.title'),
        message: [
          t('admin.settings.edit_space.delete.confirmation.message_problems'),
          t('admin.settings.edit_space.delete.confirmation.message_qr_codes'),
          t('admin.settings.edit_space.delete.confirmation.message_spaces'),
          t('admin.settings.edit_space.delete.confirmation.message_documents'),
          t(
            'admin.settings.edit_space.delete.confirmation.message_recurring_tasks'
          ),
        ],
        confirm: t('admin.settings.edit_space.delete.confirmation.delete'),
      }"
      :options="[
        {
          key: 'convert_to_asset',
          label: t('admin.settings.edit_space.convert_to_asset.label'),
          icon: Category,
          onClick: () => handleConvertToAsset(),
          show: () => hasPermission(permissions.updateSpace),
        },
        {
          key: 'copy_messenger_url',
          label: t('admin.settings.edit_space.copy_messenger_url.label'),
          icon: MessageSmileCircle,
          onClick: () => copyMessengerURL(),
        },
      ]"
    />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/TenantSettings";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import { computed, defineProps, inject, ref } from "vue";
import Tree from "@/Tailwind/Components/Settings/Spaces/Tree/Tree.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { usePage } from "@inertiajs/vue3";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import SettingsPanel from "@/Tailwind/Components/Settings/SettingsPanel.vue";
import LocationQrCodes from "@/Tailwind/Components/Settings/Spaces/LocationQrCodes.vue";
import { useI18n } from "vue-i18n";
import MessageSmileCircle from "@/Tailwind/Icon/MessageSmileCircle.vue";
import Category from "@/Tailwind/Icon/Category.vue";
import { usePreloadStore } from "@/Tailwind/Stores/preload";

const { t } = useI18n();

defineOptions({ layout: PersistentLayout });

const props = defineProps({
  space: Object,
  spaces: Array,
});

const preloadStore = usePreloadStore();

const tenant = usePage().props.tenant;
const openSpaceSlideOver = ref(!!props.space);
const isCreateLoading = ref(false);
const isUpdateLoading = ref(false);
const isDeleteLoading = ref(false);

const { hasPermission, permissions } = inject("permissions");

// Space management
const spaceFields = computed(() => [
  {
    key: "name",
    title: t("admin.settings.location.name.title"),
    description: t("admin.settings.location.name.description"),
    placeholder: t("admin.settings.location.name.placeholder"),
    type: "text",
  },
  {
    key: "identifier",
    title: t("admin.settings.location.identifier.title"),
    description: t("admin.settings.location.identifier.description"),
    placeholder: t("admin.settings.location.identifier.placeholder"),
    type: "text",
  },
  {
    key: "type",
    title: t("admin.settings.location.type.title"),
    description: t("admin.settings.location.type.description"),
    placeholder: t("admin.settings.location.type.placeholder"),
    type: "dropdown",
    options: typeOptions,
    default: "room",
    extraAttributes: {
      selectorStyle: "input",
    },
  },
  {
    key: "parent_id",
    title: t("admin.settings.location.parent_space.title"),
    description: t("admin.settings.location.parent_space.description"),
    placeholder: t("admin.settings.location.parent_space.placeholder"),
    type: "space",
    extraAttributes: {
      tenantId: tenant.id,
      selectorStyle: "dropdown",
    },
  },
  {
    // Key doesn't matter for this component
    key: "qr_codes",
    title: t("admin.settings.location.qr_codes.title"),
    description: t("admin.settings.location.qr_codes.description"),
    placeholder: t("admin.settings.location.qr_codes.placeholder"),
    component: LocationQrCodes,
    extraAttributes: {
      space: parsedLocationDetails.value,
    },
    show: () => {
      return !!props.space;
    },
  },
]);

const typeOptions = usePage().props.space_types.map((type) => ({
  value: type.value,
  label: t(`components.spaces.types.${type.value}.label`),
}));

const parsedLocationDetails = computed(() => {
  if (!props.space) {
    return {};
  }

  return {
    ...props.space,
    type: props.space.type.value,
  };
});

function handleCreateSpace(inertiaForm) {
  inertiaForm
    .transform((data) => ({
      ...data,
      parent_id: data.parent_id?.id,
    }))
    .post(route("settings.spaces.store", [tenant]), {
      onStart: () => (isCreateLoading.value = true),
      onSuccess: () => {
        handleCloseSpaceSlideOver();
        preloadStore.loadLocations(true);
      },
      onError: () => (isCreateLoading.value = false),
    });
}

function handleUpdateSpace(inertiaForm) {
  inertiaForm
    .transform((data) => {
      return {
        ...data,
        parent_id:
          typeof data.parent_id === "object"
            ? data.parent_id?.id
            : data.parent_id,
      };
    })
    .put(route("settings.spaces.update", [tenant, props.space]), {
      onStart: () => (isUpdateLoading.value = true),
      onSuccess: () => {
        handleCloseSpaceSlideOver();
        preloadStore.loadLocations(true);
      },
      onError: () => (isUpdateLoading.value = false),
    });
}

async function copyMessengerURL() {
  await navigator.clipboard.writeText(
    route("space.welcome", [props.space.tenant, props.space])
  );
}

function handleConvertToAsset() {
  router.post(
    route("settings.assets.convert", [tenant, props.space]),
    {},
    {
      onSuccess: () => {
        handleCloseSpaceSlideOver();
        preloadStore.loadLocations(true);
        preloadStore.loadAssets(true);
      },
    }
  );
}

function handleDeleteSpace() {
  if (!hasPermission(permissions.deleteSpace)) {
    return;
  }

  router.delete(route("settings.spaces.destroy", [tenant, props.space]), {
    onStart: () => (isDeleteLoading.value = true),
    onSuccess: () => {
      handleCloseSpaceSlideOver();
      preloadStore.loadLocations(true);
    },
    onError: () => (isDeleteLoading.value = false),
  });
}

// -----------------------------

function handleSpaceSelected(spaceId) {
  router.visit(route("settings.spaces.edit", [tenant, spaceId]));
}

function handleCloseSpaceSlideOver() {
  router.visit(route("settings.spaces.index", [tenant]));
}

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
